import type { MonthlyStat, TopJob, RecentActivityItem } from "@/hooks/useDashboardStats";

interface ChartSectionProps {
  monthlyStats: MonthlyStat[];
  topJobs: TopJob[];
  loading: boolean;
}

export default function ChartSection({ monthlyStats, topJobs, loading }: ChartSectionProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-background-50 rounded-xl border border-background-200/70 p-5 animate-pulse">
          <div className="h-5 bg-background-200 rounded w-1/3 mb-5"></div>
          <div className="flex items-end gap-4 h-[220px] px-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-2 h-full justify-end">
                <div
                  className="w-full max-w-[32px] bg-background-200 rounded-t-md"
                  style={{ height: `${30 + Math.random() * 60}%` }}
                ></div>
                <div className="h-3 bg-background-200 rounded w-6"></div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 animate-pulse">
          <div className="h-5 bg-background-200 rounded w-1/2 mb-4"></div>
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-background-200"></div>
                <div className="flex-1 h-4 bg-background-200 rounded"></div>
                <div className="w-8 h-4 bg-background-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const maxApps = Math.max(...monthlyStats.map((m) => m.applications), 1);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 bg-background-50 rounded-xl border border-background-200/70 p-5">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading font-semibold text-foreground-950 text-base">
            Ứng viên & Tuyển dụng theo tháng
          </h3>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-primary-400"></span>
              <span className="text-[11px] text-foreground-500">Ứng viên</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-accent-400"></span>
              <span className="text-[11px] text-foreground-500">Đã tuyển</span>
            </div>
          </div>
        </div>

        {monthlyStats.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[220px] gap-2">
            <div className="w-10 h-10 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-bar-chart-2-line text-foreground-400"></i>
            </div>
            <p className="text-sm text-foreground-500">Chưa có dữ liệu thống kê theo tháng</p>
          </div>
        ) : (
          <div className="flex items-end gap-4 h-[220px] px-2">
            {monthlyStats.map((item) => {
              const appHeight = (item.applications / maxApps) * 100;
              const hireHeight = (item.hires / maxApps) * 100;
              return (
                <div
                  key={item.month}
                  className="flex-1 flex flex-col items-center gap-2 h-full justify-end"
                >
                  <div className="w-full flex flex-col items-center gap-2">
                    <span className="text-[11px] font-semibold text-foreground-700">
                      {item.applications}
                    </span>
                    <div
                      className="w-full max-w-[32px] bg-primary-400/80 rounded-t-md transition-all hover:bg-primary-500/90 cursor-pointer"
                      style={{ height: `${appHeight}%` }}
                      title={`${item.month}: ${item.applications} ứng viên`}
                    ></div>
                    <div
                      className="w-full max-w-[24px] bg-accent-400/80 rounded-t-md transition-all hover:bg-accent-500/90 cursor-pointer -mt-1"
                      style={{ height: `${hireHeight}%` }}
                      title={`${item.month}: ${item.hires} đã tuyển`}
                    ></div>
                  </div>
                  <span className="text-[11px] font-medium text-foreground-500 pt-1">
                    {item.month}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
        <h3 className="font-heading font-semibold text-foreground-950 text-base mb-4">
          Tin tuyển dụng nổi bật
        </h3>

        {topJobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2">
            <div className="w-10 h-10 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-briefcase-line text-foreground-400"></i>
            </div>
            <p className="text-sm text-foreground-500">Chưa có tin tuyển dụng</p>
          </div>
        ) : (
          <div className="space-y-3">
            {topJobs.map((job, idx) => (
              <div key={job.id} className="flex items-center gap-3 group cursor-pointer">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 shrink-0">
                  <span className="text-xs font-bold">{idx + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                    {job.title}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-sm font-semibold text-foreground-900">{job.apps}</span>
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i
                      className={`text-xs ${
                        job.trend === "up"
                          ? "ri-arrow-up-line text-accent-500"
                          : job.trend === "down"
                          ? "ri-arrow-down-line text-red-400"
                          : "ri-subtract-line text-foreground-400"
                      }`}
                    ></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface RecentActivityProps {
  activities: RecentActivityItem[];
  loading: boolean;
}

export function RecentActivity({ activities, loading }: RecentActivityProps) {
  if (loading) {
    return (
      <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 animate-pulse">
        <div className="h-5 bg-background-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-0">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-start gap-3 py-3 border-b border-background-100">
              <div className="w-8 h-8 rounded-lg bg-background-200 shrink-0"></div>
              <div className="flex-1 space-y-1.5">
                <div className="h-4 bg-background-200 rounded w-full"></div>
                <div className="h-3 bg-background-200 rounded w-1/3"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const getIconBg = (type: string) => {
    switch (type) {
      case "new_application":
        return "bg-primary-100 text-primary-600";
      case "job_expiring":
        return "bg-secondary-100 text-secondary-600";
      case "system":
        return "bg-background-200 text-foreground-600";
      default:
        return "bg-background-100 text-foreground-500";
    }
  };

  return (
    <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-foreground-950 text-base">
          Hoạt động gần đây
        </h3>
      </div>

      {activities.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 gap-2">
          <div className="w-10 h-10 rounded-full bg-background-200 flex items-center justify-center">
            <i className="ri-history-line text-foreground-400"></i>
          </div>
          <p className="text-sm text-foreground-500">Chưa có hoạt động nào</p>
        </div>
      ) : (
        <div className="space-y-0">
          {activities.map((activity, idx) => (
            <div
              key={activity.id}
              className={`flex items-start gap-3 py-3 ${
                idx < activities.length - 1 ? "border-b border-background-100" : ""
              }`}
            >
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${getIconBg(
                  activity.type
                )}`}
              >
                <i className={`${activity.icon} text-sm`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground-800">{activity.message}</p>
                <p className="text-[11px] text-foreground-400 mt-1">{activity.time}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}